import { useRef, useState } from "react";
import { Box, Button, Divider, InputLabel, Modal, TextField, Typography } from "@mui/material";
import { modalStyles, useStyles } from "./ChangeHMICodeWarningModal.styles";
import { useTranslation } from "react-i18next";
import ReactToPrint from "react-to-print";
import { IChangeHMICodeWarningModal } from "./ChangeHMICodeWarningModalInterfaces";
import logo from "./madera_mark_logo.png";

const ChangeHMICodeWarningModal = ({ open, onClose, newHMICode, machineCenter }: IChangeHMICodeWarningModal) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement | null>(null);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [labelInfo, setLabelInfo] = useState({
        username: "",
        password: "",
        serialNumber: "",
    });

    const handleOnClose = () => {
        setShowPrintPreview(false);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleOnClose} disableEscapeKeyDown keepMounted={false}>
            <Box sx={modalStyles}>
                {!showPrintPreview && (
                    <>
                        <section className={classes.content}>
                            <div className={classes.title}>
                                <Typography variant="h3">
                                    {t(
                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.title",
                                    )}
                                </Typography>
                            </div>
                            <div className={classes.warningTextWrapper}>
                                <Typography className={classes.warningText}>
                                    {t(
                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.warning_text",
                                    )}
                                </Typography>
                            </div>
                            <div className={classes.hmiCodeWrapper}>
                                <span className={classes.hmiCode}>{newHMICode}</span>
                            </div>

                            <div className={classes.complementedTextWrapper}>
                                <Typography>
                                    {t(
                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.print_text",
                                    )}
                                </Typography>
                            </div>
                        </section>
                        <Divider />
                        <section className={classes.footer}>
                            <Button
                                color="info"
                                variant="outlined"
                                className={classes.printButton}
                                onClick={() => setShowPrintPreview(true)}
                            >
                                {t(
                                    "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.print_button",
                                )}
                            </Button>
                            <Button
                                color="info"
                                onClick={handleOnClose}
                                variant="contained"
                                className={classes.closeButton}
                            >
                                {t(
                                    "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.close_button",
                                )}
                            </Button>
                        </section>
                    </>
                )}
                {!!showPrintPreview && (
                    <>
                        <section className={classes.printPreviewWrapper}>
                            <Typography className={classes.labelPreview}>
                                {t(
                                    "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.label_preview",
                                )}
                            </Typography>
                            <div className={classes.hmiCodeWrapperPrintPreview}>
                                <div className={classes.hmiCodeTableWrapperPrintPreview}>
                                    <table cellSpacing="0">
                                        <tbody>
                                            <tr>
                                                <td rowSpan={6} className={classes.tableLogo}>
                                                    <div className={classes.tableLogoWrapperPreview}>
                                                        <img src={logo} alt="logo" />
                                                    </div>
                                                </td>
                                                <td className={classes.firstTd}>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.machine",
                                                    )}
                                                    :
                                                </td>
                                                <td>{machineCenter.short_name || machineCenter.name}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.code",
                                                    )}
                                                    :
                                                </td>
                                                <td>{newHMICode}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.username",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.username}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.password",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.password}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.serial_number",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.serialNumber}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className={classes.hmiCodeFieldsPrintPreview}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="current-username-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.username",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="current-username-label"
                                        fullWidth
                                        id="current-username-input"
                                        className={classes.textField}
                                        name="username"
                                        type="text"
                                        placeholder="user@maildomain.com"
                                        variant="outlined"
                                        value={labelInfo.username}
                                        onChange={(e) => setLabelInfo({ ...labelInfo, username: e.target.value })}
                                    />
                                </Box>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="current-password-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.password",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="current-password-label"
                                        fullWidth
                                        id="current-password-input"
                                        className={classes.textField}
                                        name="currentPassword"
                                        type="text"
                                        placeholder="********"
                                        variant="outlined"
                                        value={labelInfo.password}
                                        onChange={(e) => setLabelInfo({ ...labelInfo, password: e.target.value })}
                                    />
                                </Box>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="current-serial-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.serial_number",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="current-serial-label"
                                        fullWidth
                                        id="current-password-input"
                                        className={classes.textField}
                                        name="serialNumber"
                                        type="text"
                                        placeholder="ABC-1234-5678"
                                        variant="outlined"
                                        value={labelInfo.serialNumber}
                                        onChange={(e) => setLabelInfo({ ...labelInfo, serialNumber: e.target.value })}
                                    />
                                </Box>
                            </div>
                            <div className={classes.hmiCodeWrapperPrint} ref={ref}>
                                <div className={classes.hmiCodeTablePrintWrapper}>
                                    <table cellSpacing="0">
                                        <tbody>
                                            <tr>
                                                <td rowSpan={6} className={classes.tableLogo}>
                                                    <div className={classes.tableLogoWrapperPreview}>
                                                        <img src={logo} alt="logo" />
                                                    </div>
                                                </td>
                                                <td className={classes.firstTd}>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.machine",
                                                    )}
                                                    :
                                                </td>
                                                <td>{machineCenter.short_name || machineCenter.name}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.code",
                                                    )}
                                                    :
                                                </td>
                                                <td>{newHMICode}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.username",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.username}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.password",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.password}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t(
                                                        "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.serial_number",
                                                    )}
                                                    :
                                                </td>
                                                <td>{labelInfo.serialNumber}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                        <Divider />
                        <section className={classes.footer}>
                            <Button
                                color="info"
                                variant="outlined"
                                className={classes.printButton}
                                onClick={() => {
                                    setLabelInfo({
                                        username: "",
                                        password: "",
                                        serialNumber: "",
                                    });
                                    setShowPrintPreview(false);
                                }}
                            >
                                {t(
                                    "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.back_button",
                                )}
                            </Button>
                            <ReactToPrint
                                content={() => ref.current}
                                trigger={() => (
                                    <Button
                                        disabled={!labelInfo.username || !labelInfo.password || !labelInfo.serialNumber}
                                        color="info"
                                        onClick={handleOnClose}
                                        variant="contained"
                                        className={classes.closeButton}
                                    >
                                        {t(
                                            "corporate_backoffice.machine_center_details.change_hmi_code_warning_modal.print_button",
                                        )}
                                    </Button>
                                )}
                            />
                        </section>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default ChangeHMICodeWarningModal;
